import React, { useContext, useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import useFetchJob from "../../Hooks/jobs/useFetchJob";
import {
  ButtonWithoutIcon,
  Job_Tag_Box,
  Loader,
  WhiteBoxWrapper,
} from "../../constents/components";
import { alphanumericDate } from "../../constents/functions";
import useApplyJob from "../../Hooks/jobs/useApplyJob";
import { useSelector } from "react-redux";
import calendarIcon from "../../Assets/icons/calendarIcon.svg";
import locationIcon from "../../Assets/icons/locationIcon.svg";
import salaryIcon from "../../Assets/icons/salaryIcon.svg";
import candidateIcon from "../../Assets/icons/candidateIcon.svg";
import jobIdIcon from "../../Assets/icons/jobIdIcon.svg";
import PositionIcon from "../../Assets/icons/PositionIcon.svg";
import WorkModeIcon from "../../Assets/icons/WorkModeIcon.svg";
import BackButtonIcon from "../../Assets/svgs/BackButtonIcon.svg";
import ShareButtonIcon from "../../Assets/svgs/ShareButtonIcon.svg";
import { SmallSignUpContext } from "../../Contexts/smallSignUpContext";
import { ShowProfileContext } from "../../Contexts/showProfileContext";
import useRedirect from "../../Hooks/Auth/useRedirect";
import ErrorPopup from "./ErrorPopup";
import { formatJdToHtml } from "../../Utils/formatJdToHtml";

import { GET_JOB_SCREENING_QUESTIONS } from "../../Utils/apis";

import useFetchAppliedJobs from "../../Hooks/jobs/useFetchAppliedJobs";

const SubHeading = React.memo(({ text, classes }) => (
  <h4 className={`font-semibold ${classes ?? ""}`}>{text}</h4>
));

const BlurredEmail = ({ email }) => {
  const canvasRef = React.useRef(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !email) return;

    const ctx = canvas.getContext("2d");

    const width = 300;
    const height = 35;

    const dpr = window.devicePixelRatio || 1;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    ctx.scale(dpr, dpr);

    ctx.font = "16px Arial";
    ctx.fillStyle = "#4b5563";
    ctx.filter = "blur(5px)";
    ctx.fillText(email, 0, 23);
  }, [email]);

  return (
    <canvas
      ref={canvasRef}
      className="select-none pointer-events-none"
      draggable="false"
    />
  );
};

const FullJobDetails = () => {
  const [query] = useSearchParams();
  const { openPopUp } = useContext(SmallSignUpContext);
  const { showProfile } = useContext(ShowProfileContext);

  const [
    appliedJobsLoading,
    appliedJobsError,
    fetchedAppliedJobs,
    fetchAppliedJobs,
  ] = useFetchAppliedJobs();

  const candidateId = useSelector((store) => store?.user?.candidate_id);

  useEffect(() => {
    if (!candidateId) return;

    fetchAppliedJobs();
  }, [candidateId, fetchAppliedJobs]);

  const jobId = query.get("jid");

  const matchedAppliedJob = fetchedAppliedJobs?.find(
    (item) => String(item?.job_id) === String(jobId),
  );

  const applicationStatus = String(matchedAppliedJob?.applicant_status || "")
    .trim()
    .toLowerCase();

  const isInterviewSubmitted = [
    "interview done",
    "interview submitted",
  ].includes(applicationStatus);

  const interviewLink = matchedAppliedJob?.interview_link;

  const canStartInterviewFromDetails =
    Boolean(interviewLink) &&
    ["interview scheduled", "shortlisted"].includes(applicationStatus);

  const handleStartInterviewFromDetails = () => {
    if (interviewLink) {
      window.open(interviewLink, "_blank", "noopener,noreferrer");
    }
  };
  const [loading, error, jobData] = useFetchJob(jobId);
  const job = jobData ?? {};
  const [applyingLoading, applyingError, jobApplyFunction] = useApplyJob();
  const [showErrorPopup, setShowErrorPopup] = useState(false);

  const [screeningQuestions, setScreeningQuestions] = useState([]);
  const [screeningAnswers, setScreeningAnswers] = useState({});
  const [showScreeningModal, setShowScreeningModal] = useState(false);
  const [screeningLoading, setScreeningLoading] = useState(false);
  const [screeningError, setScreeningError] = useState("");

  useEffect(() => {
    if (applyingError) setShowErrorPopup(true);
  }, [applyingError]);
  const appliedJobs = useSelector((store) => store?.user?.appliedJobs);
  const isApplied = appliedJobs?.map((item) => item?.jobId)?.includes(jobId);
  const jobTitle = job?.job_title;
  const JobDescription = formatJdToHtml(job?.about_job?.jd);
  const companyProfileImgUrl = job?.company_details?.website_logo;
  const companyName = job?.company_details?.company_name;
  const companySummary = job?.company_details?.company_description;
  const location = Array.isArray(job?.location)
    ? job.location
    : Array.isArray(job?.address)
      ? job.address
      : [];

  const jobPostingTime = `Posted at ${alphanumericDate(job?.created_at)}`;

  const JobType = job?.about_job?.job_type ?? job?.type ?? "";

  const email = job?.company_details?.contact_email;

  const skills = Array.isArray(job?.about_job?.skills)
    ? job.about_job.skills
    : Array.isArray(job?.skills)
      ? job.skills
      : [];

  const Values = Array.isArray(job?.addons?.company_values)
    ? job.addons.company_values
    : Array.isArray(job?.companyValues)
      ? job.companyValues
      : [];

  const Work_Type =
    job?.about_job?.work_type ??
    job?.about_job?.workType ??
    job?.workType ??
    "";

  const Min_Salary =
    job?.about_job?.min_salary ?? job?.about_job?.minSalary ?? job?.minSalary;

  const Max_Salary =
    job?.about_job?.max_salary ?? job?.about_job?.maxSalary ?? job?.maxSalary;

  const Industry = job?.about_job?.industry ?? job?.industry ?? "";

  const Category = job?.about_job?.category ?? job?.category ?? [];

  const CategoryData = Array.isArray(Category) ? Category[0] : Category;

  const SubIndustry =
    CategoryData?.category ??
    job?.about_job?.subIndustry ??
    job?.about_job?.sub_industry ??
    job?.subIndustry ??
    job?.sub_industry ??
    "";

  const Roles = Array.isArray(CategoryData?.roles)
    ? CategoryData.roles
    : CategoryData?.roles
      ? [CategoryData.roles]
      : [];

  const MinExperience =
    job?.about_job?.min_experience ??
    job?.about_job?.minExperience ??
    job?.minExperience ??
    job?.min_experience;

  const MaxExperience =
    job?.about_job?.max_experience ??
    job?.about_job?.maxExperience ??
    job?.maxExperience ??
    job?.max_experience;

  const Education = job?.about_job?.education ?? job?.education ?? [];

  const EducationList = Array.isArray(Education) ? Education : [];

  const EducationNames = EducationList.map((item) =>
    typeof item === "string" ? item : item?.education,
  ).filter(Boolean);

  const Specializations = [
    ...new Set(
      EducationList.flatMap((item) =>
        typeof item === "string"
          ? []
          : Array.isArray(item?.qualifications)
            ? item.qualifications
            : [],
      ),
    ),
  ];

  const Candidates = job?.candidateApplied ?? 0;

  const source = query?.get("src");

  const WorkingDays =
    job?.about_job?.working_days ??
    job?.about_job?.workingDays ??
    job?.workingDays ??
    0;

  const urlParams = new URLSearchParams(window.location.search);
  const currentJobId = urlParams.get("jid");

  const ActiveDate =
    currentJobId === "e96d7a66-d80a-40af-975c-27c7e2b51e05"
      ? "Monday to Saturday"
      : "Monday to Friday";
  const candidate_id = useSelector((store) => store?.user?.candidate_id);
  const SortJobId = job?.short_jobid;

  const navigate = useNavigate();
  const { redirectUrlSetter } = useRedirect();

  const handleShare = async () => {};

  const fetchScreeningQuestions = async () => {
    setScreeningError("");
    setScreeningLoading(true);

    try {
      const response = await fetch(
        `${GET_JOB_SCREENING_QUESTIONS}?jobId=${encodeURIComponent(jobId)}`,
        {
          method: "GET",
          credentials: "include",
        },
      );

      const result = await response.json();

      if (!response.ok || !result?.success) {
        throw new Error(
          result?.message || "Unable to fetch screening questions.",
        );
      }

      const data = result?.data;

      if (data?.profileComplete === false) {
        const fields = data?.missingFields || [];

        showProfile({
          isApplyFlow: true,
          fields,
          onCompleted: fetchScreeningQuestions,
        });

        return;
      }

      const questions = Array.isArray(data?.questions) ? data.questions : [];

      setScreeningQuestions(questions);
      setScreeningAnswers({});

      setShowScreeningModal(true);

      if (questions.length === 0) {
        setShowScreeningModal(false);

        const success = await jobApplyFunction(jobId);

        if (!success) {
          setShowErrorPopup(true);
        }
      }
    } catch (err) {
      console.error("Screening questions fetch error:", err);
      setScreeningError(err.message || "Unable to load screening questions.");
    } finally {
      setScreeningLoading(false);
    }
  };

  const updateScreeningAnswer = (questionId, answer) => {
    setScreeningAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const getQuestionKey = (question) => question.question;

  const handleJobApply = async () => {
    if (source?.toLowerCase() === "linkedin" && !candidate_id) {
      openPopUp(jobId);
      return;
    }

    if (!candidate_id) {
      redirectUrlSetter();
      navigate("/auth/login");
      return;
    }

    setShowErrorPopup(false);
    setScreeningError("");

    await fetchScreeningQuestions();
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className="w-full  md:p-6 sm:p-2">
          <div className="flex items-center justify-between px-4 py-2 md:px-0">
            <div className="flex items-center gap-2">
              {/* Back Button */}
              <img
                src={BackButtonIcon}
                alt="Back"
                className="cursor-pointer w-9 h-9 md:w-10 md:h-10"
                onClick={() => navigate(-1)}
              />
              <div>
                <h1 className="text-lg font-semibold md:text-2xl">
                  {jobTitle}
                </h1>
                <p className="text-xs md:text-sm">{jobPostingTime}</p>
              </div>
            </div>

            <div className="flex gap-2">
              <button onClick={handleShare}>
                <img
                  src={ShareButtonIcon}
                  alt="Share Icon"
                  className="w-12 h-12 rounded-full hover:bg-gray-200 scale-120"
                />
              </button>

              {/* Apply Button Logic */}

              {/* Apply Button Logic */}
              {job?.application ? (
                canStartInterviewFromDetails ? (
                  <button
                    type="button"
                    onClick={handleStartInterviewFromDetails}
                    className="px-8 py-3 rounded-full bg-primary-color-900 text-white font-medium hover:opacity-90"
                  >
                    Start Interview
                  </button>
                ) : isInterviewSubmitted ? (
                  <ButtonWithoutIcon
                    onButtonClick={() => {}}
                    text="Interview Submitted"
                    inversed={true}
                    disabled={true}
                    classNames="px-8 py-1 text-gray-500 cursor-not-allowed"
                  />
                ) : (
                  <ButtonWithoutIcon
                    onButtonClick={() => {}}
                    text="Applied"
                    inversed={true}
                    disabled={true}
                    classNames="px-8 py-1 text-gray-500 cursor-not-allowed"
                  />
                )
              ) : (
                <ButtonWithoutIcon
                  onButtonClick={
                    job?.status === "active"
                      ? isApplied
                        ? () => {}
                        : handleJobApply
                      : () => {}
                  }
                  disabled={job?.status !== "active"}
                  text={
                    job?.status !== "active"
                      ? "Application Closed"
                      : isApplied
                        ? `Applied`
                        : applyingLoading
                          ? "Applying..."
                          : "Apply Now"
                  }
                  classNames={
                    job?.status !== "active"
                      ? "px-8 py-1 text-gray-500 bg-gray-300 cursor-not-allowed"
                      : "px-8 py-1 text-white"
                  }
                />
              )}
            </div>
          </div>

          {/* <div className='flex gap-8 mt-4 '> */}
          <div className="flex flex-col gap-6 sm:flex-row ">
            {/* Left Section - Job Description */}
            <div className="flex flex-col flex-1 w-full gap-6">
              <WhiteBoxWrapper>
                <h1 className="text-lg font-semibold">{"Job Description"}</h1>
                <div
                  className="
        text-md
        font-sans
        text-gray-700
        mt-2
        leading-7

        [&_h1]:text-xl
        [&_h1]:font-semibold
        [&_h1]:text-gray-900
        [&_h1]:mb-4

        [&_h2]:text-lg
        [&_h2]:font-semibold
        [&_h2]:text-gray-900
        [&_h2]:mt-5
        [&_h2]:mb-3

        [&_h3]:text-base
        [&_h3]:font-semibold
        [&_h3]:text-gray-900
        [&_h3]:mt-4
        [&_h3]:mb-2

        [&_p]:mb-4

        [&_ul]:list-disc
        [&_ul]:pl-6
        [&_ul]:mb-4

        [&_ol]:list-decimal
        [&_ol]:pl-6
        [&_ol]:mb-4

        [&_li]:mb-1

        [&_strong]:font-semibold
    "
                  dangerouslySetInnerHTML={{
                    __html: JobDescription || "",
                  }}
                />

                <h1 className="mt-4 text-lg font-semibold">{"Skills"}</h1>
                <div className="flex flex-wrap gap-3 mt-2">
                  {skills?.map((item, index) => (
                    <Job_Tag_Box key={index} text={item} />
                  ))}
                </div>
                <h1 className="mt-4 text-lg font-semibold">{"Values"}</h1>
                <div className="flex flex-wrap gap-3 mt-2">
                  {Values?.map((item, index) => (
                    <Job_Tag_Box key={index} text={item} />
                  ))}
                </div>
              </WhiteBoxWrapper>
            </div>

            {/* Right Section - Company Information */}
            <div className="w-full space-y-4 sm:w-1/3">
              <div className="h-fit border-[2px] border-gray-200 rounded-2xl p-4 bg-[#fff]">
                <div className="flex items-center mt-0 mb-4">
                  {companyProfileImgUrl && (
                    <img
                      className="w-10 h-10 border-[1px] rounded-full"
                      src={companyProfileImgUrl}
                      alt="companyImage"
                    />
                  )}
                  <h1 className="ml-2 text-xl font-semibold">{companyName}</h1>
                </div>

                <h3 className="font-semibold text-md">About Company</h3>
                <h1 className="mt-1 text-sm text-gray-600">{companySummary}</h1>

                <h3 className="mt-6 text-sm font-medium">Contact</h3>
                <BlurredEmail email={email} />
              </div>
              {/* Additional Information Box */}
              <div className="h-fit border-[2px] border-gray-200 rounded-2xl p-4 bg-[#fff]">
                <div className="mt-4 space-y-4">
                  {/* Sort Job Id */}
                  {SortJobId && (
                    <>
                      <div>
                        <div className="flex items-center gap-2">
                          <img
                            src={jobIdIcon}
                            alt="Job ID Icon"
                            className="w-4 h-4"
                          />
                          <SubHeading
                            text={"Job ID"}
                            classes={" mb-1 text-sm text-gray-500"}
                          />
                        </div>
                        <p className="mt-1 text-base font-medium">
                          {SortJobId}
                        </p>
                      </div>
                      <hr className="border-[1px] border-gray-200" />
                    </>
                  )}
                  {/* Location */}
                  <div>
                    <div className="flex items-center gap-2">
                      <img
                        src={locationIcon}
                        alt="Location Icon"
                        className="w-4 h-4"
                      />
                      <SubHeading
                        text={"Location"}
                        classes={" mb-1 text-sm text-gray-500"}
                      />
                    </div>
                    <p className="mt-1 text-base font-medium">
                      {location.length > 0
                        ? location.join(", ")
                        : "Not specified"}
                    </p>
                  </div>

                  <hr className="border-[1px] border-gray-200" />

                  <div>
                    <SubHeading
                      text={"Industry"}
                      classes={"mb-1 text-sm text-gray-500"}
                    />

                    <p className="mt-1 text-base font-medium">
                      {Industry || "Not specified"}
                    </p>
                  </div>

                  <hr className="border-[1px] border-gray-200" />

                  <div>
                    <SubHeading
                      text={"Sub Industry"}
                      classes={"mb-1 text-sm text-gray-500"}
                    />

                    <p className="mt-1 text-base font-medium">
                      {SubIndustry || "Others"}
                    </p>
                  </div>

                  <hr className="border-[1px] border-gray-200" />

                  <div>
                    <SubHeading
                      text={"Role"}
                      classes={"mb-1 text-sm text-gray-500"}
                    />

                    <div className="flex flex-wrap gap-2 mt-2">
                      {Roles.length > 0 ? (
                        Roles.map((role, index) => (
                          <Job_Tag_Box key={index} text={role} />
                        ))
                      ) : (
                        <p className="text-base font-medium">Others</p>
                      )}
                    </div>
                  </div>

                  <hr className="border-[1px] border-gray-200" />

                  <div>
                    <div className="flex items-center gap-2">
                      <img
                        src={PositionIcon}
                        alt="Experience Icon"
                        className="w-4 h-4"
                      />

                      <SubHeading
                        text={"Experience"}
                        classes={"mb-1 text-sm text-gray-500"}
                      />
                    </div>

                    <div className="flex gap-8 mt-2">
                      <div>
                        <p className="text-xs text-gray-500">Minimum</p>

                        <p className="text-base font-medium">
                          {MinExperience !== undefined
                            ? `${MinExperience} years`
                            : "Not specified"}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-gray-500">Maximum</p>

                        <p className="text-base font-medium">
                          {MaxExperience !== undefined
                            ? `${MaxExperience} years`
                            : "Not specified"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <hr className="border-[1px] border-gray-200" />

                  <div>
                    <SubHeading
                      text={"Education"}
                      classes={"mb-1 text-sm text-gray-500"}
                    />

                    <div className="flex flex-wrap gap-2 mt-2">
                      {EducationNames.length > 0 ? (
                        EducationNames.map((education, index) => (
                          <Job_Tag_Box key={index} text={education} />
                        ))
                      ) : (
                        <p className="text-base font-medium">Not specified</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-4">
                    <SubHeading
                      text={"Specialization"}
                      classes={"mb-1 text-sm text-gray-500"}
                    />

                    <div className="flex flex-wrap gap-2 mt-2">
                      {Specializations.length > 0 ? (
                        Specializations.map((specialization, index) => (
                          <Job_Tag_Box key={index} text={specialization} />
                        ))
                      ) : (
                        <p className="text-base font-medium">Not specified</p>
                      )}
                    </div>
                  </div>

                  <hr className="border-[1px] border-gray-200" />

                  {/* Salary */}
                  <div>
                    <div className="flex items-center gap-2">
                      <img
                        src={salaryIcon}
                        alt="Salary Icon"
                        className="w-4 h-4"
                      />
                      <SubHeading
                        text={"Salary"}
                        classes={"mb-1 text-sm text-gray-500"}
                      />
                    </div>
                    <p className="mt-1 text-base font-medium">
                      {Min_Salary} - {Max_Salary} / Year
                    </p>
                  </div>

                  <hr className="border-[1px] border-gray-200" />

                  {/* Working Days */}
                  <div>
                    <div className="flex items-center gap-2">
                      <img
                        src={calendarIcon}
                        alt="Calendar Icon"
                        className="w-4 h-4"
                      />
                      <h3 className="mb-1 text-sm font-semibold text-gray-500 ">
                        Working Days
                      </h3>
                    </div>
                    <p className="mt-1 text-base font-medium">
                      {WorkingDays}
                      {WorkingDays === 1 && " day per week"}
                      {WorkingDays > 1 && " days per week"}
                    </p>
                  </div>
                  <hr className="border-[1px] border-gray-200" />

                  {/* Work Mode and Position Sections */}
                  <div className="flex justify-between gap-4 mt-4">
                    {/* Work Mode */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <img
                          src={WorkModeIcon}
                          alt="Work Mode Icon"
                          className="w-4 h-4"
                        />
                        <h3 className="mb-1 text-sm font-semibold text-gray-500">
                          Work Mode
                        </h3>
                      </div>
                      <p className="mt-1 text-base font-medium">{Work_Type}</p>
                    </div>

                    {/* Position */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <img
                          src={PositionIcon}
                          alt="Position Icon"
                          className="w-4 h-4"
                        />
                        <h3 className="mb-1 text-sm font-semibold text-gray-500">
                          Position
                        </h3>
                      </div>
                      <p className="mt-1 text-base font-medium">{JobType}</p>
                    </div>
                  </div>
                  <hr className="border-[1px] border-gray-200 mt-4" />

                  {/* Candidates */}
                  <div>
                    <div className="flex items-center gap-2">
                      <img
                        src={candidateIcon}
                        alt="Candidate Icon"
                        className="w-4 h-4"
                      />
                      <h3 className="mb-1 text-sm font-semibold text-gray-500">
                        Candidates
                      </h3>
                    </div>
                    <p className="mt-1 text-base font-medium">{Candidates}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* </div> */}
        </div>
      )}

      {showScreeningModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Screening Questions
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Please answer the following questions to continue.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowScreeningModal(false)}
                className="text-2xl text-gray-400 hover:text-gray-700"
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            {screeningLoading ? (
              <div className="flex min-h-48 items-center justify-center">
                <Loader />
              </div>
            ) : screeningError ? (
              <div className="flex flex-col items-center gap-4 py-8">
                <p className="text-sm text-red-600 text-center">
                  {screeningError}
                </p>
                <button
                  type="button"
                  onClick={fetchScreeningQuestions}
                  className="rounded-full bg-primary-color-900 px-6 py-2 text-sm text-white"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  setScreeningError("");

                  const answersPayload = screeningQuestions.map((question) => {
                    const questionText = question.question;
                    const type = String(
                      question.response_type || "",
                    ).toUpperCase();
                    const answer = screeningAnswers[questionText] ?? "";

                    return {
                      question: questionText,
                      answer:
                        type === "NUMBER" && answer !== ""
                          ? Number(answer)
                          : String(answer),
                    };
                  });

                  const hasMissingAnswer = screeningQuestions.some(
                    (question) => {
                      const answer = screeningAnswers[question.question];

                      return (
                        question.required &&
                        (answer === undefined ||
                          answer === null ||
                          String(answer).trim() === "")
                      );
                    },
                  );

                  if (hasMissingAnswer) {
                    setScreeningError("Please answer all required questions.");
                    return;
                  }

                  const success = await jobApplyFunction(jobId, answersPayload);

                  if (success) {
                    setShowScreeningModal(false);
                  } else {
                    setShowErrorPopup(true);
                  }
                }}
              >
                <div className="flex flex-col gap-5">
                  {screeningQuestions.map((question, index) => {
                    const key = getQuestionKey(question);
                    const type = String(
                      question.response_type || "",
                    ).toUpperCase();
                    const value = screeningAnswers[key] ?? "";

                    return (
                      <div
                        key={key}
                        className="rounded-xl border border-gray-200 p-4"
                      >
                        <label className="block text-sm font-medium text-gray-800">
                          {index + 1}. {question.question}
                          {question.required && (
                            <span className="ml-1 text-red-500">*</span>
                          )}
                        </label>

                        <div className="!mt-3 !m-0 !p-0">
                          {type === "YES_NO" ? (
                            <div className="mt-3 flex flex-col items-start gap-2">
                              {["YES", "NO"].map((option) => (
                                <label
                                  key={option}
                                  className="!flex !items-center !justify-start !w-fit !m-0 !p-0 !gap-2 cursor-pointer"
                                  style={{
                                    marginLeft: "0px",
                                  }}
                                >
                                  <input
                                    type="radio"
                                    name={`question-${key}`}
                                    value={option}
                                    checked={value === option}
                                    required={Boolean(question.required)}
                                    onChange={() =>
                                      updateScreeningAnswer(key, option)
                                    }
                                    style={{
                                      margin: "0px",
                                      width: "16px",
                                      height: "16px",
                                      minWidth: "16px",
                                      flexShrink: 0,
                                    }}
                                  />

                                  <span style={{ margin: 0, padding: 0 }}>
                                    {option === "YES" ? "Yes" : "No"}
                                  </span>
                                </label>
                              ))}
                            </div>
                          ) : type === "MULTIPLE_CHOICE" ? (
                            <div className="mt-3 flex flex-col items-start gap-2">
                              {(question.options || []).map(
                                (option, optionIndex) => {
                                  const optionValue =
                                    typeof option === "string"
                                      ? option
                                      : (option?.value ?? option?.label ?? "");

                                  return (
                                    <label
                                      key={optionIndex}
                                      className="!flex !items-center !justify-start !w-fit !m-0 !p-0 !gap-2 cursor-pointer"
                                      style={{
                                        marginLeft: "0px",
                                      }}
                                    >
                                      <input
                                        type="radio"
                                        name={`question-${key}`}
                                        value={optionValue}
                                        checked={value === optionValue}
                                        required={
                                          Boolean(question.required) &&
                                          optionIndex === 0
                                        }
                                        onChange={() =>
                                          updateScreeningAnswer(
                                            key,
                                            optionValue,
                                          )
                                        }
                                        style={{
                                          margin: "0px",
                                          width: "16px",
                                          height: "16px",
                                          minWidth: "16px",
                                          flexShrink: 0,
                                        }}
                                      />

                                      <span style={{ margin: 0, padding: 0 }}>
                                        {optionValue}
                                      </span>
                                    </label>
                                  );
                                },
                              )}
                            </div>
                          ) : (
                            <input
                              type={
                                type === "NUMBER"
                                  ? "number"
                                  : type === "EMAIL"
                                    ? "email"
                                    : type === "URL"
                                      ? "url"
                                      : "text"
                              }
                              min={type === "NUMBER" ? "0" : undefined}
                              step={type === "NUMBER" ? "any" : undefined}
                              value={value}
                              required={Boolean(question.required)}
                              onChange={(e) => {
                                const inputValue = e.target.value;

                                updateScreeningAnswer(
                                  key,
                                  type === "NUMBER" && inputValue !== ""
                                    ? Number(inputValue)
                                    : inputValue,
                                );
                              }}
                              placeholder="Enter your answer"
                              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-primary-color-900"
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {screeningError && (
                  <p className="mt-4 text-sm text-red-600">{screeningError}</p>
                )}

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowScreeningModal(false)}
                    className="rounded-full border border-gray-300 px-5 py-2 text-sm text-gray-700"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={applyingLoading}
                    className="rounded-full bg-primary-color-900 px-6 py-2 text-sm text-white disabled:opacity-50"
                  >
                    {applyingLoading ? "Submitting..." : "Submit Application"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
      {showErrorPopup && (
        <ErrorPopup
          error={applyingError}
          onClose={() => setShowErrorPopup(false)}
        />
      )}
    </>
  );
};

export default FullJobDetails;
